import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { useAuthStore } from '../../stores/auth.store';
import type { BersaglioMemoria } from '../../stores/assistente.store';
import { SEZIONI_MEMORIA, schedaQuery, type RigaMemoria, type SezioneMemoria } from './assistenteApi';

/**
 * «Cosa sa MUNE di …» (PIANO.md, sezione 5, «Memoria»): il foglio, dentro il
 * pannello dell'assistente, con le righe della scheda memoria per sezione, la fonte e i
 * comandi Correggi, Togli e «Non farla cambiare a MUNE». In fondo «Aggiungi una
 * cosa da sapere». Le schede servono all'Assistente: qui le persone le controllano.
 */
export function MemoriaVista({ bersaglio }: { bersaglio: BersaglioMemoria }) {
  const qc = useQueryClient();
  const vedeCifre = useAuthStore((s) => s.user?.role.key === 'SUPER_ADMIN');
  const q = schedaQuery(bersaglio.tipo, bersaglio.id);
  const { data: scheda, isLoading } = useQuery(q);
  const ricarica = () => qc.invalidateQueries({ queryKey: q.queryKey });

  const [inModifica, setInModifica] = useState<string | null>(null);
  const [bozza, setBozza] = useState('');
  const [nuovaSezione, setNuovaSezione] = useState<SezioneMemoria>('CHI_SONO');
  const [nuovoTesto, setNuovoTesto] = useState('');

  const correggi = useMutation({
    mutationFn: (v: { id: string; testo?: string; bloccata?: boolean }) =>
      api.patch(`/ai/memoria/fatti/${v.id}`, { testo: v.testo, bloccata: v.bloccata }),
    onSuccess: () => { setInModifica(null); ricarica(); },
    onError: (e: any) => toast.alert('Non salvato', e?.message ?? 'Riprova'),
  });

  const togli = useMutation({
    mutationFn: (id: string) => api.delete(`/ai/memoria/fatti/${id}`),
    onSuccess: (_r, id) => {
      ricarica();
      toast.undo('Riga tolta dalla scheda', {
        onUndo: () => { api.post(`/ai/memoria/fatti/${id}/ripristina`).then(ricarica).catch(() => toast.alert('Non rimessa', 'Riprova')); },
      });
    },
    onError: (e: any) => toast.alert('Non tolta', e?.message ?? 'Riprova'),
  });

  const aggiungi = useMutation({
    mutationFn: () =>
      api.post('/ai/memoria/fatti', {
        ...(bersaglio.tipo === 'cliente' ? { clientId: bersaglio.id } : { projectId: bersaglio.id }),
        sezione: nuovaSezione,
        testo: nuovoTesto,
        // Le regole le scrivono le persone: l'Assistente non le cambia.
        bloccata: nuovaSezione === 'REGOLE',
      }),
    onSuccess: () => { setNuovoTesto(''); ricarica(); toast.ok('Aggiunta alla scheda'); },
    onError: (e: any) => toast.alert('Non aggiunta', e?.message ?? 'Riprova'),
  });

  const sezioni = SEZIONI_MEMORIA.filter((s) => s.codice !== 'CIFRE' || vedeCifre);
  const righe = scheda?.righe ?? [];

  const riga = (r: RigaMemoria) => (
    <li key={r.id} className="mem-riga">
      {inModifica === r.id ? (
        <div className="mem-modifica">
          <textarea className="ast-campo" value={bozza} onChange={(e) => setBozza(e.target.value)} rows={3} autoFocus />
          <div className="mem-comandi-testo">
            <button className="btn btn-sm btn-primary" disabled={!bozza.trim() || correggi.isPending} onClick={() => correggi.mutate({ id: r.id, testo: bozza })}>
              Salva
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => setInModifica(null)}>Lascia com'è</button>
          </div>
        </div>
      ) : (
        <>
          <div className="mem-testo">{r.testo}</div>
          <div className="mem-meta">
            {r.fonte}
            {r.dedotta && ' · deduzione'}
            {r.bloccata && ' · bloccata'}
          </div>
          <div className="mem-azioni">
            <button className="btn-icon" title="Correggi" aria-label="Correggi" onClick={() => { setInModifica(r.id); setBozza(r.testo); }}>
              <Icon name="pencil" size={12} />
            </button>
            <button
              className="btn-icon"
              title={r.bloccata ? 'Lascia che MUNE la cambi' : 'Non farla cambiare a MUNE'}
              aria-label={r.bloccata ? 'Lascia che MUNE la cambi' : 'Non farla cambiare a MUNE'}
              aria-pressed={r.bloccata}
              onClick={() => correggi.mutate({ id: r.id, bloccata: !r.bloccata })}
            >
              <Icon name="lock" size={12} />
            </button>
            <button className="btn-icon" title="Togli" aria-label="Togli" onClick={() => togli.mutate(r.id)}>
              <Icon name="trash" size={12} />
            </button>
          </div>
        </>
      )}
    </li>
  );

  return (
    <div className="mem">
      <p className="mem-spiega">
        MUNE legge queste righe prima di ogni risposta su {bersaglio.nome}. Se una è sbagliata, correggila o toglila.
      </p>

      {isLoading ? null : !righe.length ? (
        <p className="mem-vuota">MUNE non sa ancora niente di {bersaglio.nome}. Aggiungi la prima cosa da sapere qui sotto.</p>
      ) : (
        sezioni.map((s) => {
          const della = righe.filter((r) => r.sezione === s.codice);
          if (!della.length) return null;
          return (
            <section key={s.codice} className="mem-sezione">
              <h3 className="mem-titolo">{s.nome}</h3>
              <ul className="mem-righe">{della.map(riga)}</ul>
            </section>
          );
        })
      )}

      <form
        className="mem-aggiungi"
        onSubmit={(e) => { e.preventDefault(); if (nuovoTesto.trim()) aggiungi.mutate(); }}
      >
        <h3 className="mem-titolo">Aggiungi una cosa da sapere</h3>
        <select className="inp" value={nuovaSezione} onChange={(e) => setNuovaSezione(e.target.value as SezioneMemoria)} aria-label="Sezione">
          {sezioni.map((s) => (
            <option key={s.codice} value={s.codice}>{s.nome}</option>
          ))}
        </select>
        <textarea
          className="ast-campo"
          rows={2}
          value={nuovoTesto}
          placeholder="Per esempio: con loro si scrive col tu"
          onChange={(e) => setNuovoTesto(e.target.value)}
        />
        <button type="submit" className="btn btn-sm btn-primary" disabled={!nuovoTesto.trim() || aggiungi.isPending}>
          Aggiungi
        </button>
      </form>
    </div>
  );
}
